import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ASSETS } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { GraduationCap, Award, Download, FileText, BookOpen, ArrowRight, LifeBuoy, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

export default function StudentDashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [tickets, setTickets] = useState([]);

  const load = () => {
    api.get('/enrollments/me').then(({ data }) => setEnrollments(data));
    api.get('/certificates').then(({ data }) => setCerts(data));
    api.get('/support/tickets/me').then(({ data }) => setTickets(data));
  };
  useEffect(load, []);

  const downloadCert = async (num) => {
    try {
      const res = await api.get(`/certificates/${num}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${num}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Could not download certificate'); }
  };

  return (
    <div className="min-h-screen bg-trjlight" data-testid="student-dashboard">
      <div className="bg-navy text-white py-12 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Student Dashboard</div>
            <h1 className="font-display font-semibold text-3xl lg:text-4xl mt-1">Welcome, {user?.name?.split(' ')[0]}.</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard/transcript" data-testid="open-transcript"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-gold">My Transcript</Button></Link>
            <Link to="/programs"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-gold" data-testid="enroll-more">Browse Programs</Button></Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 -mt-8 relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Enrolled Courses" value={enrollments.length} icon={BookOpen} />
          <StatCard label="In Progress" value={enrollments.filter(e => !e.is_complete && e.progress_pct > 0).length} icon={GraduationCap} />
          <StatCard label="Certificates" value={certs.length} icon={Award} accent />
          <StatCard label="Open Tickets" value={tickets.filter(t => t.status === 'open').length} icon={LifeBuoy} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <Tabs defaultValue="courses">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="courses" data-testid="tab-courses">My Courses</TabsTrigger>
            <TabsTrigger value="certificates" data-testid="tab-certs">Certificates</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads">Downloads</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">Support</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            {enrollments.length === 0 ? (
              <Empty title="No active enrollments yet" body="Browse our programs to begin your TRJ Academy journey." cta="/programs" ctaLabel="Browse Programs" image={ASSETS.seminar} />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {enrollments.map(e => (
                  <Link key={e.course_id} to={`/learn/${e.course_id}`} data-testid={`enrollment-${e.course_id}`}
                    className="card-elevate bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
                    <div className="aspect-[16/9] bg-navy/5">
                      {e.course_image && <img src={e.course_image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="font-display font-semibold text-navy">{e.course_title}</h3>
                      <div className="mt-3 flex-1">
                        <div className="text-xs flex justify-between text-slate-500 mb-1.5">
                          <span>Progress</span><span className="font-semibold text-navy">{e.progress_pct}%</span>
                        </div>
                        <Progress value={e.progress_pct} className="h-2" />
                      </div>
                      <div className="mt-4 text-sm font-semibold text-navy inline-flex items-center">
                        {e.is_complete ? 'Review Course' : 'Continue Learning'} <ArrowRight className="ml-1 h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="certificates" className="mt-6">
            {certs.length === 0 ? (
              <Empty title="No certificates yet" body="Earn certificates by completing courses and passing assessments." cta="/programs" ctaLabel="Browse Programs" image={ASSETS.graduation} />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {certs.map(c => (
                  <div key={c.certificate_number} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`cert-${c.certificate_number}`}>
                    <Award className="h-10 w-10 text-gold" />
                    <div className="mt-3 text-xs uppercase tracking-widest text-gold font-semibold">{c.certificate_number}</div>
                    <h3 className="font-display font-semibold text-navy mt-1 leading-snug">{c.course_title}</h3>
                    <div className="text-xs text-slate-500 mt-2">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
                    <Button className="btn-navy w-full h-10 mt-5" onClick={() => downloadCert(c.certificate_number)} data-testid={`download-${c.certificate_number}`}>
                      <Download className="h-4 w-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="downloads" className="mt-6">
            <div className="bg-white border border-slate-200 rounded-xl p-8">
              <h3 className="font-display font-semibold text-lg text-navy">Course materials</h3>
              <p className="text-slate-600 mt-2">Open any enrolled course to access lesson PDFs, slides, and supplemental resources.</p>
              {enrollments.length > 0 && (
                <div className="mt-5 space-y-3">
                  {enrollments.map(e => (
                    <Link to={`/learn/${e.course_id}`} key={e.course_id} className="flex items-center justify-between bg-trjlight rounded-md p-3 hover:bg-navy/5">
                      <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-navy" /><span className="text-navy font-medium">{e.course_title}</span></div>
                      <ArrowRight className="h-4 w-4 text-navy" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="font-display font-semibold text-lg text-navy">Need help?</h3>
                <p className="text-sm text-slate-600 mt-2">Open a ticket and our team will respond within 1 business day.</p>
                <NewTicketDialog onCreated={load} />
              </div>
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="font-display font-semibold text-lg text-navy">Your tickets</h3>
                <div className="mt-4 space-y-3">
                  {tickets.length === 0 && <div className="text-sm text-slate-500">No tickets yet.</div>}
                  {tickets.map(t => (
                    <div key={t.ticket_id} className="border border-slate-200 rounded-md p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-navy">{t.subject}</div>
                        <span className="text-xs uppercase tracking-widest bg-gold/10 text-gold px-2 py-1 rounded">{t.status}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">{t.message}</p>
                      <div className="text-xs text-slate-400 mt-2">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <div className="bg-white border border-slate-200 rounded-xl p-8" data-testid="announcements-panel">
              <div className="flex items-center gap-3"><Bell className="h-5 w-5 text-gold" /><h3 className="font-display font-semibold text-lg text-navy">Academy announcements</h3></div>
              <div className="mt-5 space-y-4 text-sm text-slate-600">
                <div className="border-l-4 border-gold pl-4">
                  <div className="font-semibold text-navy">Welcome to TRJ Academy</div>
                  <p className="mt-1">We are continuously adding new programs and CE content. Watch this space for updates.</p>
                </div>
                <div className="border-l-4 border-navy/30 pl-4">
                  <div className="font-semibold text-navy">Tax season prep</div>
                  <p className="mt-1">Enroll early in foundational programs to be ready for the upcoming tax season.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl p-5 border ${accent ? 'bg-navy text-white border-navy' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest ${accent ? 'text-gold' : 'text-slate-500'}`}>{label}</div>
        <Icon className={`h-4 w-4 ${accent ? 'text-gold' : 'text-navy'}`} />
      </div>
      <div className={`text-3xl font-display font-semibold mt-3 ${accent ? '' : 'text-navy'}`}>{value}</div>
    </div>
  );
}

function Empty({ title, body, cta, ctaLabel, image }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
      {image && <img src={image} alt="" className="h-40 w-full object-cover rounded-md opacity-90" />}
      <h3 className="font-display font-semibold text-xl text-navy mt-6">{title}</h3>
      <p className="text-slate-600 mt-2 max-w-md mx-auto">{body}</p>
      {cta && <Link to={cta}><Button className="btn-navy mt-5">{ctaLabel}</Button></Link>}
    </div>
  );
}

function NewTicketDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    await api.post('/support/tickets', { subject, message });
    toast.success('Ticket opened');
    setOpen(false); setSubject(''); setMessage(''); onCreated && onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-gold w-full mt-5" data-testid="open-ticket-btn">Open New Ticket</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Support Ticket</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="ticket-subject" /></div>
          <div><Label>Describe the issue</Label><Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="ticket-message" /></div>
          <Button type="submit" className="btn-navy w-full" data-testid="ticket-submit">Submit</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
